import axios from '@/lib/axios'
import {
	QueryClient,
	useMutation,
	useQuery,
	useQueryClient,
} from '@tanstack/vue-query'
import { create } from 'lodash'
import { ToastInterface, useToast } from 'vue-toastification'

import { Email } from '@/interfaces/email'

interface Props {
	immediate?: boolean
	params?: Params
}

interface Params extends Omit<Partial<Email>, 'id'> {
	id: string | number
	emailId?: string | number
}

type CreateParams = Omit<Required<Params>, 'emailId'>

const getUserEmail = async (params?: Params): Promise<Email | void> => {
	if (params) {
		const { id, emailId } = params
		const { data } = await axios.get(`/api/users/${id}/emails/${emailId}`)

		return data
	}
}

const createUserEmail = async (
	params: CreateParams,
	toast: ToastInterface,
	queryClient: QueryClient
): Promise<Email | undefined> => {
	try {
		const { id } = params
		const { data } = await axios.post(`/api/users/${id}/emails`, params)

		queryClient.invalidateQueries(['user', id])
		toast.success('Email creado con éxito')
		return data
	} catch (err: any) {
		toast.error(
			err.response?.data?.message ||
				'Error al crear el email. Intente nuevamente.'
		)
	}
}

const deleteUserEmail = async (
	params: Required<Pick<Params, 'id' | 'emailId'>>,
	toast: ToastInterface,
	queryClient: QueryClient
) => {
	try {
		const { id, emailId } = params
		await axios.delete(`/api/users/${id}/emails/${emailId}`)

		queryClient.invalidateQueries(['user', id])
		toast.success('Email eliminado con éxito')
	} catch (err: any) {
		toast.error(
			err.response?.data?.message ||
				'Error al eliminar el email. Intente nuevamente.'
		)
		return err
	}
}

const useUserEmail = (props?: Props) => {
	const toast = useToast()
	const { immediate = true, params } = props || {}
	const queryClient = useQueryClient()

	const createMutation = useMutation((params: CreateParams) =>
		createUserEmail(params, toast, queryClient)
	)

	const deleteMutation = useMutation(
		(params: Required<Pick<Params, 'id' | 'emailId'>>) =>
			deleteUserEmail(params, toast, queryClient)
	)

	const { data, isLoading } = useQuery(
		['userEmail'],
		() => getUserEmail(params),
		{ enabled: immediate }
	)

	return {
		data,
		isLoading,

		createEmailMutation: createMutation,
		createEmail: createMutation.mutate,
		createEmailAsync: createMutation.mutateAsync,
		createEmailLoading: createMutation.isLoading,

		deleteEmailMutation: deleteMutation,
		deleteEmail: deleteMutation.mutate,
		deleteEmailAsync: deleteMutation.mutateAsync,
	}
}

export default useUserEmail
